import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Avatar } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import axios from 'axios';
import { OpenAPI } from '../../api/generated/requests/core/OpenAPI';

interface LazyThumbnailProps {
  filename: string;
  size?: number;
  quality?: number;
}

const LazyThumbnail: React.FC<LazyThumbnailProps> = ({ 
  filename, 
  size = 50, 
  quality = 85 
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let currentUrl: string | null = null;

    const loadThumbnail = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // Make direct axios request with proper responseType for binary data
        const response = await axios.get(`${OpenAPI.BASE}/api/files/${encodeURIComponent(filename)}/thumbnail`, {
          params: {
            size,
            quality
          },
          responseType: 'arraybuffer',
          headers: {
            ...OpenAPI.HEADERS
          },
          withCredentials: OpenAPI.WITH_CREDENTIALS
        });

        if (isMounted) {
          // Response.data contains the ArrayBuffer with binary image data
          const blob = new Blob([response.data], { type: 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          currentUrl = url;
          setThumbnailUrl(url);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading thumbnail:', error);
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    // Cleanup function
    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [filename, size, quality]);

  // Cleanup effect for thumbnailUrl when component unmounts
  useEffect(() => {
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [thumbnailUrl]);

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          width: size, 
          height: size, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (hasError || !thumbnailUrl) {
    return (
      <Avatar 
        sx={{ 
          width: size, 
          height: size, 
          bgcolor: 'grey.300' 
        }}
      >
        <ImageIcon fontSize="small" />
      </Avatar>
    );
  }

  return (
    <Box
      component="img"
      src={thumbnailUrl}
      alt={`Thumbnail for ${filename}`}
      sx={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid #e0e0e0'
      }}
    />
  );
};

export default LazyThumbnail;
